import type { SignatorySelection } from './CompanySignatoryBlock';
import { normalizeSignerRole } from './ContractSignPage.helpers';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BRAZIL_COUNTRY_CODE = '55';
const BRAZIL_MOBILE_PATTERN = /^[1-9]{2}9\d{8}$/;

const toNationalDigits = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    return digits.length === 13 && digits.startsWith(BRAZIL_COUNTRY_CODE) ? digits.slice(BRAZIL_COUNTRY_CODE.length) : digits;
};

const toMobileDigits = (value: string): string | null => {
    const digits = toNationalDigits(value);
    return BRAZIL_MOBILE_PATTERN.test(digits) ? digits : null;
};

export interface SignatoryBlockRef {
    companyName: string;
    companyCnpj: string;
    participantSource: 'ASSIGNOR' | 'RECIPIENT';
}

export const blockKeyOf = (block: SignatoryBlockRef): string =>
    `${block.participantSource}-${block.companyCnpj.replace(/\D/g, '')}`;

/** E-mail deste envio: o editado na seleção ou, sem edição, o do cadastro (AF-4138). */
export const resolveSignerEmail = (selection: SignatorySelection): string =>
    (selection.emailOverride ?? selection.signatory.email ?? '').trim();

/** E-mail aceito para o envio, depois de aparar os espaços. */
export const isValidSignerEmail = (value: string): boolean => EMAIL_PATTERN.test(value.trim());

/** Celular deste envio (AF-4181), em dígitos (DDD + número); vazio quando ausente ou inválido. */
export const resolveSignerPhone = (selection: SignatorySelection): string =>
    toMobileDigits(selection.phoneOverride ?? selection.signatory.mobile ?? '') ?? '';

/** Celular aceito para o envio: vazio, ou DDD + 9 dígitos começando por 9, com ou sem +55. */
export const isValidSignerMobile = (value: string): boolean =>
    value.trim() === '' || toMobileDigits(value) !== null;

/** Celular no formato da máscara da tela, (DD) NNNNN-NNNN, a partir de qualquer formato do cadastro. */
export const formatMobileMask = (value: string): string => {
    const digits = toNationalDigits(value);
    if (!digits) {
        return '';
    }
    const ddd = digits.slice(0, 2);
    const prefix = digits.slice(2, 7);
    const suffix = digits.slice(7, 11);
    return `(${ddd}${digits.length > 2 ? ') ' : ''}${prefix}${suffix ? `-${suffix}` : ''}`;
};

/** Celular editado neste envio: o digitado difere do cadastro, sem contar espaços. */
export const isPhoneEdited = (selection: SignatorySelection): boolean =>
    selection.phoneOverride !== undefined && toNationalDigits(selection.phoneOverride) !== toNationalDigits(selection.signatory.mobile ?? '');

/** Algum selecionado com celular editado e inválido: bloqueia confirmar (o do cadastro não bloqueia). */
export const hasSelectedWithInvalidEditedPhone = (selections: SignatorySelection[]): boolean =>
    selections.some(s => s.selected && isPhoneEdited(s) && !isValidSignerMobile(s.phoneOverride ?? ''));

/**
 * Signatários selecionados nos quadrados, no formato do PUT /contracts/{id} e do
 * validate-for-signature (contrato: services/contracts/validate-for-signature-request.schema.json).
 * Cada um leva o lado do quadrado de onde foi escolhido, que define a ordem de assinatura.
 */
export const buildSignersPayload = (
    blocks: SignatoryBlockRef[],
    selections: Record<string, SignatorySelection[]>,
): Record<string, unknown>[] => blocks.flatMap(block => {
    const selectedInBlock = (selections[blockKeyOf(block)] || []).filter(s => s.selected);
    return selectedInBlock.map((sel, idx) => ({
        // When the PDF name diverges from the Person record, the operator chooses which one goes
        // to the provider. Defaults to the Person record.
        name: sel.nameSource === 'document' && sel.pdfName ? sel.pdfName : sel.signatory.fullName,
        email: resolveSignerEmail(sel),
        phone: resolveSignerPhone(sel),
        role: normalizeSignerRole(sel.contractRole),
        govTaxId: (sel.signatory.socialId || '').replace(/\D/g, ''),
        participant_source: block.participantSource,
        anchor_name: block.companyName,
        anchor_index: idx,
        anchor_total: selectedInBlock.length,
        person_verified_at: sel.signatory.lastVerifiedAt || undefined,
        use_digital_certificate: sel.useDigitalCertificate || false,
    }));
});
